'use client';

import { dashboardPathHref } from '@/lib/routing/platform-hosts';

import { AlertTriangle, RotateCcw } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

import { Button } from '@/components/ui/button';
import { useReportRouteError } from '@/lib/client/observability/use-report-route-error';

export default function RootError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const pathname = usePathname();

  useReportRouteError(error, { route: pathname });

  useEffect(() => {
    console.error('[app] unhandled route error', error);
  }, [error]);

  return (
    <div className="relative min-h-screen overflow-hidden bg-background text-foreground">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,90,60,0.12),_transparent_42%),radial-gradient(circle_at_85%_15%,_rgba(15,15,15,0.08),_transparent_36%)]" />

      <main className="relative mx-auto flex min-h-screen w-full max-w-6xl items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <section className="w-full max-w-2xl rounded-[var(--pub-r-xl)] border border-border/80 bg-card/95 p-8 shadow-[0_20px_70px_rgba(0,0,0,0.15)] backdrop-blur">
          <div className="inline-flex items-center gap-2 rounded-full border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.12em] text-[var(--color-warning)] text-[var(--color-warning)]">
            <AlertTriangle className="h-3.5 w-3.5" />
            Request Interrupted
          </div>

          <h1 className="mt-5 text-3xl font-semibold tracking-tight">Something failed before the page could finish loading.</h1>
          <p className="mt-3 text-sm leading-7 text-muted-foreground">
            Menuraq stopped the request safely. Retry the view, and if the problem persists, sign in again or refresh the
            page.
          </p>

          {error?.digest ? (
            <p className="mt-4 rounded-[var(--pub-r-sm)] border border-border/70 bg-muted/30 px-4 py-3 font-mono text-xs text-muted-foreground">
              Reference: {error.digest}
            </p>
          ) : null}

          <div className="mt-6 flex flex-wrap gap-3">
            <Button onClick={reset}>
              <RotateCcw className="h-4 w-4" />
              Retry
            </Button>
            <Button variant="outline" onClick={() => window.location.assign(dashboardPathHref('/dashboard'))}>
              Open Dashboard
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
}
